import { useGameStore } from '../../../store/gameStore';
import { useTelegram } from '../../../hooks/useTelegram';
import { cn } from '../../../lib/cn';
import type { CellValue } from '../../../../../shared/types';

export default function NumberPad() {
  const { haptic } = useTelegram();
  const currentGame = useGameStore(state => state.currentGame);
  const selectedCell = useGameStore(state => state.selectedCell);
  const makeMove = useGameStore(state => state.makeMove);
  const clearCell = useGameStore(state => state.clearCell);
  const toggleCellNote = useGameStore(state => state.toggleCellNote);
  const getCellNotes = useGameStore(state => state.getCellNotes);
  const isNoteMode = useGameStore(state => state.isNoteMode);
  useGameStore(state => state.cellNotes); // Subscribe to cellNotes changes for reactivity

  if (!currentGame || currentGame.status === 'completed') return null;

  // Calculate remaining count for each number (1-9)
  // Each number should appear exactly 9 times in solution
  const getRemainingCount = (num: number): number => {
    let countInSolution = 0;
    let countInCurrent = 0;
    
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (currentGame.solution[row][col] === num) {
          countInSolution++;
        }
        if (currentGame.currentState[row][col] === num) {
          countInCurrent++;
        }
      }
    }
    
    // Should be 9, but calculate difference to be safe
    return Math.max(0, countInSolution - countInCurrent);
  };

  const handleNumberClick = (num: CellValue) => {
    if (!selectedCell) return;
    
    // Check if cell is given (from puzzle)
    if (currentGame.puzzle[selectedCell.row][selectedCell.col] !== 0) {
      haptic('rigid');
      return;
    }

    haptic('light');
    
    if (isNoteMode) {
      // In note mode, toggle note (add/remove note)
      toggleCellNote(selectedCell.row, selectedCell.col, num);
    } else {
      // Normal mode: set value
      makeMove(selectedCell.row, selectedCell.col, num);
    }
  };

  const handleClear = () => {
    if (!selectedCell) return;
    
    if (currentGame.puzzle[selectedCell.row][selectedCell.col] !== 0) {
      haptic('rigid');
      return;
    }

    haptic('light');
    
    // Always check for notes first, regardless of mode
    const notes = getCellNotes(selectedCell.row, selectedCell.col);
    if (notes.size > 0) {
      // Clear all notes
      notes.forEach(note => {
        toggleCellNote(selectedCell.row, selectedCell.col, note);
      });
    } else {
      // No notes, clear cell value
      clearCell(selectedCell.row, selectedCell.col);
    }
  };


  return (
    <div className="grid grid-cols-5 gap-2">
      {/* Numbers 1-5 (first row) */}
      {[1, 2, 3, 4, 5].map((num) => {
        const cellNotes = selectedCell ? getCellNotes(selectedCell.row, selectedCell.col) : new Set<number>();
        const hasNote = isNoteMode && cellNotes.has(num);
        const remaining = getRemainingCount(num);
        
        return (
          <button
            key={num}
            onClick={() => handleNumberClick(num as CellValue)}
            disabled={!selectedCell}
            className={cn(
              'relative aspect-square rounded-lg border-2 border-border',
              'flex items-center justify-center',
              'text-2xl font-bold transition-all',
              'active:scale-95',
              'hover:bg-accent hover:border-primary',
              !selectedCell && 'opacity-50 cursor-not-allowed',
              hasNote && 'bg-primary/20 border-primary'
            )}
          >
            <span>{num}</span>
            {remaining > 0 && (
              <span className="absolute top-1 right-1 text-[9px] font-normal text-muted-foreground leading-none">
                {remaining}
              </span>
            )}
          </button>
        );
      })}

      {/* Numbers 6-9 (second row) */}
      {[6, 7, 8, 9].map((num) => {
        const cellNotes = selectedCell ? getCellNotes(selectedCell.row, selectedCell.col) : new Set<number>();
        const hasNote = isNoteMode && cellNotes.has(num);
        const remaining = getRemainingCount(num);
        
        return (
          <button
            key={num}
            onClick={() => handleNumberClick(num as CellValue)}
            disabled={!selectedCell}
            className={cn(
              'relative aspect-square rounded-lg border-2 border-border',
              'flex items-center justify-center',
              'text-2xl font-bold transition-all',
              'active:scale-95',
              'hover:bg-accent hover:border-primary',
              !selectedCell && 'opacity-50 cursor-not-allowed',
              hasNote && 'bg-primary/20 border-primary'
            )}
          >
            <span>{num}</span>
            {remaining > 0 && (
              <span className="absolute top-1 right-1 text-[9px] font-normal text-muted-foreground leading-none">
                {remaining}
              </span>
            )}
          </button>
        );
      })}

      {/* Clear button - after 9 */}
      <button
        onClick={handleClear}
        disabled={!selectedCell}
        className={cn(
          'aspect-square rounded-lg border-2 border-border',
          'flex items-center justify-center',
          'font-medium transition-all text-lg',
          'hover:bg-accent hover:border-primary',
          'active:scale-95',
          !selectedCell && 'opacity-50 cursor-not-allowed'
        )}
      >
        ✕
      </button>
    </div>
  );
}

